import { useEffect, useMemo, useRef, useState } from "react";
import { Mic, Square, RotateCcw, CheckCircle2, Info, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import ListenButton from "./ListenButton";

const MASTERY_THRESHOLD = 80;

/**
 * ReadAloud — the learner taps Record, reads the passage, taps Stop, and we
 * ask Whisper to transcribe. We compare the transcript to the expected words
 * and highlight what they nailed (green) vs. what to retry (soft red).
 *
 * Mastery gate: once a learner has recorded at least one attempt, we require
 * them to reach {MASTERY_THRESHOLD}% accuracy before the parent lesson can be
 * marked complete. `onGateChange` fires whenever the gate state changes so
 * the parent lesson knows to lock/unlock its "Mark complete" button.
 *
 * We use MediaRecorder → webm/opus (universally supported in Safari 17+, all
 * Chromium, all Firefox). Audio is uploaded as multipart/form-data.
 */
export default function ReadAloud({ expectedText, onGateChange }) {
    const [status, setStatus] = useState("idle"); // idle | recording | uploading | done | error
    const [seconds, setSeconds] = useState(0);
    const [result, setResult] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");
    const [attempts, setAttempts] = useState(0);
    const [bestAccuracy, setBestAccuracy] = useState(0);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);

    const mastered = bestAccuracy >= MASTERY_THRESHOLD;
    const locked = attempts > 0 && !mastered;

    // Broadcast gate changes upward so <Lesson /> can lock its complete button.
    useEffect(() => {
        onGateChange?.({ locked, mastered, bestAccuracy, attempts });
    }, [locked, mastered, bestAccuracy, attempts, onGateChange]);

    useEffect(() => {
        return () => {
            // Stop any in-flight recorder + timer on unmount.
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                try {
                    recorderRef.current.stop();
                } catch { /* noop */ }
            }
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    // Tricky words = the words the learner missed on their MOST RECENT attempt,
    // deduped and normalized. Kept up here (above the early !supported return)
    // so hook ordering stays stable across renders.
    const trickyWords = useMemo(() => {
        if (!result?.words) return [];
        const seen = new Set();
        const out = [];
        for (const w of result.words) {
            if (w.hit) continue;
            const clean = String(w.word || "").replace(/[^A-Za-z'-]/g, "").trim();
            if (!clean) continue;
            const key = clean.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(clean);
            if (out.length >= 8) break;
        }
        return out;
    }, [result]);

    const supported =
        typeof window !== "undefined" &&
        typeof navigator !== "undefined" &&
        navigator.mediaDevices?.getUserMedia &&
        typeof MediaRecorder !== "undefined";

    if (!supported) {
        return (
            <div
                className="rounded-xl border border-[#E5E7E2] bg-[#F5F1E4] p-5 text-[#515B57]"
                data-testid="read-aloud-unsupported"
            >
                Recording isn&apos;t supported in this browser. Try the app in Chrome, Safari, or Firefox.
            </div>
        );
    }

    const start = async () => {
        setErrorMsg("");
        setResult(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mime =
                MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                    ? "audio/webm;codecs=opus"
                    : MediaRecorder.isTypeSupported("audio/mp4")
                        ? "audio/mp4"
                        : "";
            const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
                await upload(blob, recorder.mimeType);
            };
            recorder.start();
            recorderRef.current = recorder;
            setStatus("recording");
            setSeconds(0);
            timerRef.current = setInterval(() => {
                setSeconds((s) => {
                    // Auto-stop at 60 seconds so learners don't overshoot Whisper's cap.
                    if (s >= 60) {
                        try {
                            recorder.stop();
                        } catch { /* noop */ }
                        clearInterval(timerRef.current);
                        return s;
                    }
                    return s + 1;
                });
            }, 1000);
        } catch (e) {
            setStatus("error");
            setErrorMsg(
                e?.name === "NotAllowedError"
                    ? "Microphone access was blocked. You can turn it on in your browser settings."
                    : "Couldn't start the microphone. Please try again.",
            );
        }
    };

    const stop = () => {
        if (recorderRef.current && recorderRef.current.state === "recording") {
            recorderRef.current.stop();
        }
        if (timerRef.current) clearInterval(timerRef.current);
    };

    const upload = async (blob, mime) => {
        setStatus("uploading");
        const ext = (mime || "").includes("mp4") ? "m4a" : "webm";
        const form = new FormData();
        form.append("audio", blob, `reading.${ext}`);
        form.append("expected_text", expectedText || "");
        try {
            const { data } = await api.post("/read-aloud", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            setStatus("done");
            const acc = data.accuracy ?? 0;
            setAttempts((n) => n + 1);
            setBestAccuracy((b) => Math.max(b, acc));
            if (acc >= MASTERY_THRESHOLD) {
                toast.success(`Nice reading — ${acc}% match`, {
                    description: bestAccuracy < MASTERY_THRESHOLD ? "You can move on now." : undefined,
                });
            } else if (acc >= 50) {
                toast(`${acc}% — one more read, aim for ${MASTERY_THRESHOLD}%`);
            } else {
                toast(`${acc}% — try it once more, you've got this`);
            }
        } catch (e) {
            setStatus("error");
            setErrorMsg(
                e?.response?.data?.detail ||
                    "We couldn't transcribe that recording. Please try again.",
            );
        }
    };

    const reset = () => {
        setResult(null);
        setStatus("idle");
        setSeconds(0);
    };

    const showTime = `0:${String(seconds).padStart(2, "0")}`;

    return (
        <div
            className="rounded-2xl border border-[#E5E7E2] bg-white p-6"
            data-testid="read-aloud"
            data-mastered={mastered ? "true" : "false"}
            data-locked={locked ? "true" : "false"}
        >
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <p className="label-eyebrow mb-1">Read it aloud · aim for {MASTERY_THRESHOLD}%</p>
                    <p className="text-[#515B57] text-sm leading-relaxed">
                        Tap record, read the passage above, then tap stop. We&apos;ll show which words
                        landed and which to try again. Hit <span className="font-medium text-[#1C2321]">{MASTERY_THRESHOLD}%</span> to move on.
                    </p>
                </div>
                {status === "done" && (
                    <div
                        className="text-3xl font-semibold text-[#2D5D4E]"
                        style={{ fontFamily: "Outfit" }}
                        data-testid="read-aloud-accuracy"
                    >
                        {result?.accuracy ?? 0}%
                    </div>
                )}
            </div>

            {/* Mastery gate banner: locked (must retry) or unlocked (passed). */}
            {locked && (
                <div
                    className="mb-5 p-4 rounded-lg bg-[#FBE7DA] border border-[#F0C6AC] text-[#8F3636] flex items-start gap-3"
                    data-testid="read-aloud-gate-locked"
                >
                    <Info className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                    <div>
                        <p className="font-medium text-[#8F3636]">Read it once more to move on.</p>
                        <p className="text-sm text-[#8F3636]/85 mt-0.5">
                            Best so far: <span className="font-semibold">{bestAccuracy}%</span>. Aim for {MASTERY_THRESHOLD}% or higher — the highlighted words below are what to focus on.
                        </p>
                    </div>
                </div>
            )}
            {mastered && (
                <div
                    className="mb-5 p-4 rounded-lg bg-[#E0EDE6] border border-[#B7D5C4] text-[#1F4237] flex items-start gap-3"
                    data-testid="read-aloud-gate-passed"
                >
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                    <div>
                        <p className="font-medium text-[#1F4237]">You hit {bestAccuracy}%. Nicely done.</p>
                        <p className="text-sm text-[#1F4237]/85 mt-0.5">
                            You can mark the lesson complete now, or read it again for the practice.
                        </p>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                {status === "idle" && (
                    <button
                        onClick={start}
                        data-testid="read-aloud-start"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237] transition-colors"
                    >
                        <Mic className="w-4 h-4" strokeWidth={1.8} /> Record
                    </button>
                )}
                {status === "recording" && (
                    <button
                        onClick={stop}
                        data-testid="read-aloud-stop"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#B24C4C] text-white font-medium hover:bg-[#8F3636] transition-colors"
                    >
                        <Square className="w-4 h-4 fill-current" strokeWidth={1.8} /> Stop · {showTime}
                    </button>
                )}
                {status === "uploading" && (
                    <span className="text-[#515B57]" data-testid="read-aloud-uploading">
                        Listening carefully…
                    </span>
                )}
                {status === "done" && (
                    <button
                        onClick={reset}
                        data-testid="read-aloud-retry"
                        className={
                            locked
                                ? "inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237] transition-colors"
                                : "inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] font-medium hover:bg-[#E8E2D2]"
                        }
                    >
                        <RotateCcw className="w-4 h-4" strokeWidth={1.8} /> {locked ? "Read it again" : "Try again"}
                    </button>
                )}
                {status === "error" && (
                    <>
                        <p className="text-[#B24C4C]" data-testid="read-aloud-error">
                            {errorMsg}
                        </p>
                        <button
                            onClick={reset}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] font-medium hover:bg-[#E8E2D2]"
                        >
                            <RotateCcw className="w-4 h-4" strokeWidth={1.8} /> Reset
                        </button>
                    </>
                )}
            </div>

            {result && (
                <div className="mt-6" data-testid="read-aloud-feedback">
                    <p className="label-eyebrow mb-3">Your read</p>
                    <div className="flex flex-wrap gap-1.5 text-lg leading-[1.9]">
                        {result.words.map((w, i) => (
                            <span
                                key={i}
                                data-testid={`read-aloud-word-${i}`}
                                data-hit={w.hit}
                                className={
                                    w.hit
                                        ? "px-2 py-0.5 rounded bg-[#E0EDE6] text-[#1F4237]"
                                        : "px-2 py-0.5 rounded bg-[#FBE7DA] text-[#8F3636] underline decoration-dotted underline-offset-4"
                                }
                                style={{ fontFamily: "Outfit" }}
                            >
                                {w.word}
                            </span>
                        ))}
                    </div>
                    <p className="text-sm text-[#515B57] mt-4">
                        {result.hits} of {result.total} words landed.
                        {result.accuracy >= 90 && (
                            <span className="text-[#2D5D4E] font-medium">
                                {" "}
                                <CheckCircle2 className="inline w-4 h-4 -mt-0.5" strokeWidth={2} /> Beautiful reading.
                            </span>
                        )}
                    </p>
                </div>
            )}

            {/* Tricky-word drill: only when the gate is locked and we have misses.
                Learners can tap each word to hear it, or sound it out phoneme by
                phoneme, before their next read. */}
            {locked && trickyWords.length > 0 && (
                <div
                    className="mt-6 p-5 rounded-xl border border-[#F0C6AC] bg-[#FFF6EF]"
                    data-testid="tricky-word-drill"
                >
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-[#FDE9DD] flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Sparkles className="w-5 h-5 text-[#D47A55]" strokeWidth={1.6} />
                        </div>
                        <div>
                            <p className="label-eyebrow mb-1" style={{ color: "#D47A55" }}>
                                Drill the tricky words
                            </p>
                            <p className="text-sm text-[#515B57] leading-relaxed">
                                Tap <span className="font-medium text-[#1C2321]">Listen</span> to hear a word, or <span className="font-medium text-[#1C2321]">Sound it out</span> to hear each phoneme first. Then read the passage again.
                            </p>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {trickyWords.map((word) => (
                            <div
                                key={word}
                                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white border border-[#F0C6AC]"
                                data-testid={`tricky-word-${word.toLowerCase()}`}
                            >
                                <span className="text-xl font-medium text-[#1C2321]" style={{ fontFamily: "Outfit" }}>
                                    {word}
                                </span>
                                <div className="flex items-center gap-2">
                                    <ListenButton
                                        text={word}
                                        mode="text"
                                        size="sm"
                                        label="Listen"
                                        testId={`tricky-listen-${word.toLowerCase()}`}
                                    />
                                    <ListenButton
                                        text={word}
                                        mode="sound_out"
                                        size="sm"
                                        label="Sound it out"
                                        testId={`tricky-sound-out-${word.toLowerCase()}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
